import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Palette, Plus, Trash2, Save, X, Lock, Globe } from 'lucide-react';

const EMPTY = { name: '', hex: '#4B1838', tier_restricted: true };

export default function AdminFounderContent() {
  const [colourways, setColourways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // colourway obj or EMPTY (creating)
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/colourways');
      setColourways((data.colourways || []).sort((a, b) => (a.order_index || 0) - (b.order_index || 0)));
    } catch { toast.error('Failed to load colourways'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => setEditing({ ...EMPTY });
  const startEdit = (c) => setEditing({ ...c });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.name.trim()) { toast.error('Name is required'); return; }
    if (!/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(editing.hex)) { toast.error('Hex must be like #4B1838'); return; }
    setSaving(true);
    try {
      const payload = { name: editing.name.trim(), hex: editing.hex, tier_restricted: !!editing.tier_restricted };
      if (editing.order_index !== undefined) payload.order_index = editing.order_index;
      if (editing.id) {
        await api.put(`/admin/colourways/${editing.id}`, payload);
        toast.success(`${editing.name} updated`);
      } else {
        await api.post('/admin/colourways', payload);
        toast.success(`${editing.name} created`);
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/colourways/${c.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const moveOrder = async (c, delta) => {
    const next = (c.order_index || 0) + delta;
    if (next < 1) return;
    try {
      await api.put(`/admin/colourways/${c.id}`, {
        name: c.name, hex: c.hex, tier_restricted: !!c.tier_restricted, order_index: next,
      });
      load();
    } catch (e) { toast.error('Reorder failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-founder-content">
      <div className="h-panel-thin p-6">
        <div className="h-eyebrow">Founder Content</div>
        <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Colourways & Founder Exclusives</div>
        <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>
          Manage the colours founders can choose from. Toggle Founder Exclusive to keep a colourway locked to
          Founders Club members only.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div className="h-eyebrow" style={{ color: 'var(--heritage-plum)' }}>
          {colourways.length} colourway{colourways.length === 1 ? '' : 's'}
        </div>
        <button onClick={startCreate} className="h-btn text-xs" data-testid="colourway-new">
          <Plus size={14} className="mr-1" /> New colourway
        </button>
      </div>

      {loading && <div className="h-eyebrow">Loading...</div>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {colourways.map((c) => (
          <Panel key={c.id} className="p-4" data-testid={`colourway-row-${c.name}`}>
            <div className="w-full h-24 rounded-lg" style={{ background: c.hex, border: '1px solid rgba(75,24,56,0.15)' }} />
            <div className="mt-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="h-serif text-lg leading-tight" style={{ color: 'var(--heritage-plum)' }}>{c.name}</div>
                <div className="text-xs opacity-70 h-serif" style={{ color: 'var(--heritage-plum)' }}>{c.hex.toUpperCase()}</div>
              </div>
              <span className="text-[10px] h-serif tracking-wider uppercase rounded-full px-2 py-0.5 flex items-center gap-1 whitespace-nowrap"
                    style={{ background: c.tier_restricted ? 'rgba(75,24,56,0.15)' : 'rgba(91,110,44,0.15)', color: 'var(--heritage-plum)' }}>
                {c.tier_restricted ? <><Lock size={10} /> Founder</> : <><Globe size={10} /> Public</>}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button onClick={() => startEdit(c)} className="h-btn-outline text-xs" data-testid={`colourway-edit-${c.name}`}>Edit</button>
              <button onClick={() => moveOrder(c, -1)} className="h-btn-outline text-xs" aria-label="move up">↑</button>
              <button onClick={() => moveOrder(c, 1)} className="h-btn-outline text-xs" aria-label="move down">↓</button>
              <button onClick={() => remove(c)} className="h-btn-outline text-xs ml-auto"
                      style={{ borderColor: '#9A2C2C', color: '#9A2C2C' }} data-testid={`colourway-delete-${c.name}`}>
                <Trash2 size={12} />
              </button>
            </div>
            <div className="text-[10px] opacity-60 mt-1" style={{ color: 'var(--heritage-plum)' }}>Order #{c.order_index}</div>
          </Panel>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(75,24,56,0.5)' }} onClick={cancel}>
          <div className="w-full max-w-lg rounded-2xl p-6" onClick={(e) => e.stopPropagation()}
               style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} data-testid="colourway-editor">
            <div className="flex items-center justify-between">
              <div>
                <div className="h-eyebrow flex items-center gap-2"><Palette size={12} /> {editing.id ? 'Edit colourway' : 'New colourway'}</div>
                <div className="h-display text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>
                  {editing.name || 'Untitled'}
                </div>
              </div>
              <button onClick={cancel} className="opacity-70 hover:opacity-100" style={{ color: 'var(--heritage-plum)' }}><X size={18} /></button>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-xs opacity-80 mb-1" style={{ color: 'var(--heritage-plum)' }}>Name</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. BLUEY (55)"
                  className="w-full rounded-md px-3 py-2 text-sm" style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#F8F4EE', color: 'var(--heritage-plum)' }} data-testid="colourway-name" />
              </div>
              <div>
                <label className="block text-xs opacity-80 mb-1" style={{ color: 'var(--heritage-plum)' }}>Hex colour</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={editing.hex} onChange={(e) => setEditing({ ...editing, hex: e.target.value })}
                    className="w-14 h-10 rounded-md cursor-pointer" style={{ border: '1px solid rgba(75,24,56,0.3)' }} data-testid="colourway-hex-picker" />
                  <input value={editing.hex} onChange={(e) => setEditing({ ...editing, hex: e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}` })}
                    className="flex-1 rounded-md px-3 py-2 text-sm h-serif" style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#F8F4EE', color: 'var(--heritage-plum)' }} data-testid="colourway-hex" />
                </div>
                <div className="mt-3 rounded-lg h-16" style={{ background: editing.hex, border: '1px solid rgba(75,24,56,0.15)' }} />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!editing.tier_restricted} onChange={(e) => setEditing({ ...editing, tier_restricted: e.target.checked })} data-testid="colourway-founder-exclusive" />
                <span className="text-sm h-serif" style={{ color: 'var(--heritage-plum)' }}>Founder Exclusive (locked to Founders Club members)</span>
              </label>
              {editing.id && (
                <div>
                  <label className="block text-xs opacity-80 mb-1" style={{ color: 'var(--heritage-plum)' }}>Display order</label>
                  <input type="number" min="1" value={editing.order_index || 1} onChange={(e) => setEditing({ ...editing, order_index: parseInt(e.target.value, 10) || 1 })}
                    className="w-32 rounded-md px-3 py-2 text-sm" style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#F8F4EE', color: 'var(--heritage-plum)' }} />
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={cancel} className="h-btn-outline text-xs" data-testid="colourway-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="h-btn text-xs" data-testid="colourway-save">
                <Save size={12} className="mr-1" /> {saving ? 'Saving...' : (editing.id ? 'Save changes' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
